/**
 * Modals that are reachable from the shell rather than from one screen.
 *
 * Hold and Recall are on the top bar, so they have to work from the Reports
 * screen as well as from the till. The POS page renders its own copies for the
 * in-page buttons; whichever is mounted responds, and only one ever is, because
 * `ui.modal` holds a single value.
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import Modal from '../components/ui/Modal';
import { Field, Input } from '../components/ui/Field';
import { RecallDialog } from '../components/pos/PosDialogs';
import { closeModal, selectModal } from '../store/uiSlice';
import { loadFromOrder } from '../store/cartSlice';
import { orderApi, authApi } from '../services/endpoints';

/* --------------------------- change password / PIN ------------------------ */

const ChangePasswordDialog = ({ open, onClose }) => {
    const [tab, setTab] = useState('password');
    const [form, setForm] = useState({ current: '', next: '', confirm: '', pin: '' });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            if (tab === 'password') {
                if (form.next !== form.confirm) throw new Error('The two new passwords do not match.');
                await authApi.changePassword(form.current, form.next);
                toast.success('Password changed.');
            } else {
                await authApi.setPin(form.pin);
                toast.success('PIN set.');
            }
            setForm({ current: '', next: '', confirm: '', pin: '' });
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Change password or PIN"
            width="sm"
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </>
            )}
        >
            <div className="mb-4 flex gap-2">
                <button
                    type="button"
                    onClick={() => setTab('password')}
                    className={tab === 'password' ? 'btn-primary btn-sm flex-1' : 'btn-secondary btn-sm flex-1'}
                >
                    Password
                </button>
                <button
                    type="button"
                    onClick={() => setTab('pin')}
                    className={tab === 'pin' ? 'btn-primary btn-sm flex-1' : 'btn-secondary btn-sm flex-1'}
                >
                    Till PIN
                </button>
            </div>

            {tab === 'password' ? (
                <div className="space-y-3">
                    <Field label="Current password" required>
                        <Input
                            type="password"
                            value={form.current}
                            onChange={(e) => setForm({ ...form, current: e.target.value })}
                            data-autofocus
                        />
                    </Field>
                    <Field label="New password" hint="At least 8 characters." required>
                        <Input
                            type="password"
                            value={form.next}
                            onChange={(e) => setForm({ ...form, next: e.target.value })}
                        />
                    </Field>
                    <Field label="Confirm new password" required>
                        <Input
                            type="password"
                            value={form.confirm}
                            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                        />
                    </Field>
                </div>
            ) : (
                <Field
                    label="Till PIN"
                    hint="4 to 6 digits. Used to sign in at the till without a keyboard."
                    required
                >
                    <Input
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        className="tabular text-center text-2xl tracking-[0.5em]"
                        value={form.pin}
                        onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })}
                        data-autofocus
                    />
                </Field>
            )}
        </Modal>
    );
};

/* ---------------------------------- shell --------------------------------- */

const GlobalModals = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { modal } = useSelector(selectModal);

    const isOnTill = location.pathname === '/pos';

    /**
     * Recalling from another screen takes the user back to the till, which is
     * the only place an order can be worked on.
     */
    const onRecall = async (orderId) => {
        try {
            const detail = await orderApi.get(orderId);
            dispatch(loadFromOrder(detail));
            dispatch(closeModal());
            navigate('/pos');
            toast.success(`Recalled ${detail.order.Order_Number}.`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <>
            {/* The POS page owns these while it is mounted, so they are only
                rendered here for the rest of the app. */}
            {!isOnTill && (
                <RecallDialog
                    open={modal === 'recall'}
                    onClose={() => dispatch(closeModal())}
                    onRecall={onRecall}
                />
            )}

            <ChangePasswordDialog
                open={modal === 'changePassword'}
                onClose={() => dispatch(closeModal())}
            />
        </>
    );
};

export default GlobalModals;
